export const PaymentProviderType = {
  PAYPAL: "paypal",
  CUSTOM_PROVIDER: "custom-provider",
  STRIPE: "stripe",
  NMI: "nmi",
  WHITE_LABEL: "white-label",
  SQUARE: "square",
  AUTHORIZE_NET: "authorize-net",
  ADYEN: "adyen",
  MERCADO_PAGO: "mercado-pago",
} as const;

export type EntityChannelType = "forms" | "surveys";

export interface PaymentProviderConfig {
  _id?: string;
  publishableKey?: string;
  paypalPublishableKey?: string;
  merchantGatewayId?: string;
  clientKey?: string;
  publicKey?: string;
}

export interface ApiPaymentProvider {
  provider: string;
  providerId?: string;
  title: string;
  imageUrl: string;
  description?: string;
  providerConfig?: PaymentProviderConfig | null;
}

export interface EntityProvidersApiResponse {
  allConnectedProviders?: ApiPaymentProvider[];
  providers?: Partial<Record<EntityChannelType | string, ApiPaymentProvider[]>>;
  defaultPaymentProvider?: ApiPaymentProvider;
  expressPaymentProvider?: ApiPaymentProvider;
}

export interface DisplayPaymentProvider {
  provider: string;
  providerId: string;
  title: string;
  imageUrl: string;
  description?: string;
}

const getProviderId = (provider: ApiPaymentProvider): string | null => {
  return provider.providerId || provider.providerConfig?._id || null;
};

/**
 * Filters connected providers to those with required config per provider type.
 * Mirrors payment platform UIPaymentProviderSelector.getValidConnectedProviders.
 */
export const getValidConnectedProviders = (
  providers: ApiPaymentProvider[] | undefined
): ApiPaymentProvider[] => {
  if (!providers?.length) return [];

  return providers.filter((provider) => {
    const { provider: providerType, providerConfig } = provider || {};
    if (!providerType || !providerConfig) return false;

    switch (providerType) {
      case PaymentProviderType.PAYPAL:
        return !!providerConfig.paypalPublishableKey;
      case PaymentProviderType.CUSTOM_PROVIDER:
      case PaymentProviderType.STRIPE:
        return !!providerConfig.publishableKey;
      case PaymentProviderType.NMI:
      case PaymentProviderType.WHITE_LABEL:
      case PaymentProviderType.SQUARE:
      case PaymentProviderType.AUTHORIZE_NET:
        return !!providerConfig.merchantGatewayId;
      case PaymentProviderType.ADYEN:
        return !!providerConfig.clientKey;
      case PaymentProviderType.MERCADO_PAGO:
        return !!providerConfig.publicKey;
      default:
        return false;
    }
  });
};

const buildConnectedProviderMap = (
  validProviders: ApiPaymentProvider[]
): Record<string, ApiPaymentProvider> => {
  const map: Record<string, ApiPaymentProvider> = {};
  validProviders.forEach((provider) => {
    const id = getProviderId(provider);
    if (id) {
      map[id] = provider;
    }
  });
  return map;
};

const shouldExcludePayPal = (channel: EntityChannelType): boolean => channel === "surveys";

const filterPayPalForChannel = (
  providerIds: string[],
  channel: EntityChannelType,
  paypalProviderId: string | undefined
): string[] => {
  if (!shouldExcludePayPal(channel) || !paypalProviderId) {
    return providerIds;
  }
  return providerIds.filter((id) => id !== paypalProviderId);
};

/**
 * Builds default provider IDs from defaultPaymentProvider and expressPaymentProvider (PayPal).
 */
export const getDefaultProviderIds = (
  defaultPaymentProvider: ApiPaymentProvider | undefined,
  expressPaymentProvider: ApiPaymentProvider | undefined,
  connectedMap: Record<string, ApiPaymentProvider>
): string[] => {
  const ids: string[] = [];

  const defaultProviderId = defaultPaymentProvider
    ? getProviderId(defaultPaymentProvider)
    : null;
  if (defaultProviderId && connectedMap[defaultProviderId]) {
    ids.push(defaultProviderId);
  }

  const paypalProviderId = expressPaymentProvider?.providerConfig?._id;
  if (
    paypalProviderId &&
    expressPaymentProvider?.providerConfig?.paypalPublishableKey &&
    connectedMap[paypalProviderId]
  ) {
    ids.push(paypalProviderId);
  }

  return ids;
};

/**
 * Resolves which providers to display for forms or surveys.
 * Uses entity-specific providers when present; otherwise falls back to defaults.
 */
export const resolveProvidersForChannel = (
  data: EntityProvidersApiResponse | null | undefined,
  channel: EntityChannelType
): DisplayPaymentProvider[] => {
  if (!data) return [];

  const validConnected = getValidConnectedProviders(data.allConnectedProviders);
  const connectedMap = buildConnectedProviderMap(validConnected);
  const paypalProviderId = data.expressPaymentProvider?.providerConfig?._id;

  let providerIds: string[] = [];
  const channelProviders = data.providers?.[channel];

  if (channelProviders?.length) {
    providerIds = channelProviders
      .map((provider) => getProviderId(provider))
      .filter((id): id is string => !!id && !!connectedMap[id]);
    providerIds = filterPayPalForChannel(providerIds, channel, paypalProviderId);
  }

  if (!providerIds.length) {
    const defaultIds = getDefaultProviderIds(
      data.defaultPaymentProvider,
      data.expressPaymentProvider,
      connectedMap
    );
    providerIds = filterPayPalForChannel(defaultIds, channel, paypalProviderId);
  }

  return providerIds
    .map((id) => connectedMap[id])
    .filter((provider): provider is ApiPaymentProvider => !!provider)
    .map((provider) => {
      const providerId = getProviderId(provider);
      return {
        provider: provider.provider,
        providerId: providerId ?? provider.provider,
        title: provider.title,
        imageUrl: provider.imageUrl,
        description: provider.description,
      };
    });
};
